import type { ComponentProps } from 'react';

import { WarningMessages } from 'constants/Warning.constants';

import {
  selectActiveAttributes,
  updateAttribute,
  variantAttributeValuesSelectors,
} from '@polyai/stream/store/reducers/variant';
import {
  selectAttributes,
  selectVariantManagement,
  selectVariants,
} from '@polyai/stream/store/reducers/variant/selectors';
import { VariantAttributesIcon } from '@polyai/ui/components/atoms/Icons';
import { PopupMenuItem } from '@polyai/ui/components/molecules/PopupMenu';

import { VARIANT_ATTRIBUTE_PREFIX } from 'components/molecules/LexicalEditor/features/action/constants';
import type {
  ActionTagContentProps,
  ActionTypeInput,
  ActionTypeOutput,
} from 'components/molecules/LexicalEditor/features/action/types';
import { useActionsContext } from 'components/molecules/RichTextInputV2';
import { useServerDispatch } from 'components/organisms/AgentStream/context/AgentStreamContext';
import { useAppSelector } from 'hooks/reduxHooks';
import VariantModal from 'screens/VariantManagement/components/VariantModal';
import { useViewMode } from 'screens/withPermissions/useViewMode';

const VariantAttributeTagContent = ({
  id,
  closePopup,
  remove,
}: ActionTagContentProps) => {
  const variants = useAppSelector(selectVariants);
  const attributes = useAppSelector(selectActiveAttributes);
  const { variantAttributeValues } = useAppSelector(selectVariantManagement);
  const serverDispatch = useServerDispatch();

  const { isEditable } = useViewMode();

  const attribute = attributes.find(a => a.id === id);

  return (
    <>
      {attribute && (
        <VariantModal
          existingNames={attributes.map(a => a.name)}
          fieldConfig={variants.reduce(
            (vs, variant) => ({
              ...vs,
              [variant.id]: {
                label: variant.name,
                value: variantAttributeValuesSelectors.selectById(
                  variantAttributeValues,
                  variant.id,
                )?.values[attribute.id],
              },
            }),
            {} as ComponentProps<typeof VariantModal>['fieldConfig'],
          )}
          id={id}
          originalName={attribute.name}
          readonly={!isEditable}
          trigger={
            <PopupMenuItem>{isEditable ? 'Edit' : 'View'}</PopupMenuItem>
          }
          type="attribute"
          onSubmit={async (name, values) => {
            await serverDispatch(
              updateAttribute({
                id,
                name,
                variantValues: { values },
              }),
            );
            closePopup?.();
          }}
        />
      )}

      {isEditable && (
        <PopupMenuItem isDelete onClick={remove}>
          Remove
        </PopupMenuItem>
      )}
    </>
  );
};

export const useVariantAttributeActionType = ({
  id,
}: ActionTypeInput): ActionTypeOutput => {
  const { isVariantAttributesEnabled } = useActionsContext();
  const attributes = useAppSelector(selectAttributes);

  const variantAttribute = attributes.find(a => a.id === id);

  const warning =
    !variantAttribute || variantAttribute.archived
      ? WarningMessages.ATTRIBUTE_NOT_EXIST
      : undefined;

  return {
    Content: VariantAttributeTagContent,
    Icon: VariantAttributesIcon,
    name: variantAttribute?.name ?? id,
    warning: warning,
    prefix: VARIANT_ATTRIBUTE_PREFIX,
    isEnabled: isVariantAttributesEnabled,
    isLoading: false,
  };
};
